import React from 'react';

import { getHikingTypesAsync } from '@apps/Hiking/@services/hiking.service';
import { IHikingType } from '@apps/Hiking/@types/hiking.types';
import PageLoader from '@components/PageLoader';
import PohAlert, { AlertVariant } from '@ui-kit/Alert';
import PohButton, { ButtonVariant } from '@ui-kit/Button';
import PohCheckbox from '@ui-kit/Checkbox';
import PohList from '@ui-kit/List';
import { showModal } from '@ui-kit/Modal';

interface IHikingTypeSelectorProps {
  multiple: boolean;
  everyoneIsNobody?: boolean;
  selectedIds?: IHikingType['id'][];
  onSelect: (selected: IHikingType[]) => void;
}

const HikingTypeSelector: React.FC<IHikingTypeSelectorProps> = ({
  multiple,
  onSelect,
  selectedIds,
  everyoneIsNobody,
}) => {
  const [selected, setSelected] = React.useState<number[]>(selectedIds ?? []);
  const [hikingTypes, setHikingTypes] = React.useState<IHikingType[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [err, setErr] = React.useState<Error>();

  React.useEffect(() => {
    const init = async () => {
      try {
        setIsLoading(true);
        const typesData = await getHikingTypesAsync();
        setHikingTypes(typesData);
      } catch (e) {
        setErr(e as Error);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, []);

  if (isLoading) return <PageLoader />;
  if (err) return <PohAlert variant={AlertVariant.Danger}>{err.message}</PohAlert>;

  return (
    <div>
      {multiple && (
        <div className="text-end">
          <small>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelected(
                  selected.length === hikingTypes.length ? [] : hikingTypes.map((x) => x.id),
                );
              }}
            >
              {selected.length === hikingTypes.length ? 'Снять выделение' : 'Выбрать все'}
            </a>
          </small>
        </div>
      )}
      <PohList
        data={hikingTypes.map((type) => ({
          key: type.id,
          content: (
            <div className="d-flex align-items-baseline" style={{ gap: 6, cursor: 'pointer' }}>
              {multiple && <PohCheckbox checked={selected.includes(type.id)} />}
              {type.name}
            </div>
          ),
          active: !multiple && selected.includes(type.id),
          action: !multiple,
          onClick: multiple
            ? () => {
                setSelected((c) =>
                  !c.includes(type.id) ? [...c, type.id] : c.filter((f) => f !== type.id),
                );
              }
            : () => onSelect(selected.includes(type.id) ? [] : [type]),
        }))}
      />
      {multiple && (
        <footer className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
          <small className="text-muted">Выбрано: {selected.length}</small>
          <PohButton
            variant={selected.length ? ButtonVariant.Primary : undefined}
            onClick={() =>
              onSelect(
                everyoneIsNobody && selected.length === hikingTypes.length
                  ? []
                  : hikingTypes.filter((t) => selected.includes(t.id)),
              )
            }
          >
            Сохранить
          </PohButton>
        </footer>
      )}
    </div>
  );
};

export function selectHikingTypeOneAsync({
  selected,
}: {
  selected?: IHikingType['id'];
}): Promise<IHikingType | null> {
  return new Promise((resolve) => {
    showModal({
      size: 'sm',
      title: 'Выбор типа похода',
      children: (closeHandler: () => void) => (
        <HikingTypeSelector
          multiple={false}
          selectedIds={selected ? [selected] : undefined}
          onSelect={([s]) => {
            resolve(s ?? null);
            closeHandler();
          }}
        />
      ),
    });
  });
}

export function selectHikingTypesSeveralAsync({
  selected,
  everyoneIsNobody,
}: {
  selected?: IHikingType['id'][];
  everyoneIsNobody?: boolean;
}): Promise<IHikingType[]> {
  return new Promise((resolve) => {
    showModal({
      size: 'sm',
      title: 'Выбор типов похода',
      children: (closeHandler: () => void) => (
        <HikingTypeSelector
          multiple={true}
          selectedIds={selected}
          everyoneIsNobody={everyoneIsNobody}
          onSelect={(s) => {
            resolve(s);
            closeHandler();
          }}
        />
      ),
    });
  });
}
